import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Printer, CheckCircle, Loader2 } from 'lucide-react'

type QueueItem = {
  id: string
  orderId: string
  recipientName: string
  recipientAddress: string
  vertical: string
  status: string
  createdAt: string
}

export function PrintQueuePage() {
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'print-queue'],
    queryFn: async () => {
      const res = await fetch('/api/admin/print-queue')
      if (!res.ok) throw new Error('Failed to load print queue')
      return res.json()
    },
  })

  const markPrinted = useMutation({
    mutationFn: async (orderId: string) => {
      const res = await fetch(`/api/admin/orders/${orderId}/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!res.ok) throw new Error('Failed to update order')
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'print-queue'] }),
  })

  const items = (data as { items: QueueItem[] } | undefined)?.items ?? []

  return (
    <div className="container max-w-5xl py-8">
      <div className="mb-8 flex items-center gap-3">
        <Printer className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Print Queue</h1>
          <p className="text-muted-foreground">
            Orders ready for printing and posting.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending Items ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{(error as Error).message}</p>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12">
              <CheckCircle className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Print queue is empty.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">
                      {item.orderId.slice(0, 8)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{item.recipientName}</p>
                        <p className="text-xs text-muted-foreground">{item.recipientAddress}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{item.vertical}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(item.createdAt).toLocaleDateString('en-AU')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markPrinted.mutate(item.orderId)}
                        disabled={markPrinted.isPending}
                      >
                        {markPrinted.isPending ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : (
                          <Printer className="mr-1 h-3 w-3" />
                        )}
                        Mark Printed
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
